import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import {
  includesOption,
  indexOfOption,
  searchWithName,
} from '../../../helpers';
import {
  SearchContainerV2,
  SearchInput,
  TableContainer,
} from '../../common/styles';
import DataTables from '../../common/components/DataTables/DataTables';
import { upcomingTopupsHeadColumns } from '../../common/components/DataTables/components/headColumns';
import ErrorPage from '../../common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  topupsStatusEnum,
} from '../../common/components/constants';
import StatusFilter from '../../common/components/StatusFilter';

const UpcomingTopups = ({
  currentFlow,
  selectedWorkspace,
  upcomingTopupsData,
  fetchingUpcomingTopups,
  fetchedUpcomingTopups,
  getUpcomingTopups,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const keyFields = ['program_name'];
  const [search, onInputChange] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const upcomingTopups = upcomingTopupsData;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [2],
      label: 'GENERATED_UPCOMING',
      disabled: true,
    },
    {
      value: [3],
      label: 'FUNDS_AWAITED_AMOUNT_DUE',
      disabled: true,
    },
    {
      value: [4],
      label: 'SCHEDULED_AMOUNT_PARKED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const statusInitiatedFilter = val => {
    if (
      val.total_amount.value !== 0 &&
      val.status !== topupsStatusEnum.PROCESSING_IN_PROGRESS
    ) {
      return true;
    }
    return false;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getUpcomingTopups({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingUpcomingTopups && fetchedUpcomingTopups) {
      if (upcomingTopups) {
        upcomingTopups.sort(
          (a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date),
        );
        const filtererdUpcomingTopus = upcomingTopups.filter(
          statusInitiatedFilter,
        );
        if (filtererdUpcomingTopus.length === 0) {
          setDisabledSearch(true);
        }
        setTableData(filtererdUpcomingTopus);
        setSearchResults(filtererdUpcomingTopus);
        setCurrentData(filtererdUpcomingTopus.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingUpcomingTopups]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingUpcomingTopups) {
        const arr = searchWithName(upcomingTopups, search, keyFields);
        const initiatedFilteredArr = arr.filter(statusInitiatedFilter);
        const availableStatuses = [];
        initiatedFilteredArr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = initiatedFilteredArr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(initiatedFilteredArr);
        }
        handlePageChange(1);
      }
    }, 500);
    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingUpcomingTopups]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getUpcomingTopups({
      workspaceId: selectedWorkspace.workspace_id,
      flowType: currentFlow,
    });
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by meal plan name'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by meal plan name
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilter
          isDisabled={disabledSearch}
          clearFilters={clearFilters}
          currentFilters={currentFilters}
          options={filterStatusOptions}
          handleSelect={handleSelect}
        />
      </SearchContainerV2>
      <TableContainer>
        {!fetchedUpcomingTopups ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            data={currentData}
            columns={upcomingTopupsHeadColumns}
            loading={fetchingUpcomingTopups || loading}
            pagination
            handlePageChange={handlePageChange}
            totalRows={searchResults ? searchResults.length : 0}
            onSort={handleSort}
            emptyStateTitle='No upcoming top-ups'
          />
        )}
      </TableContainer>
    </>
  );
};

UpcomingTopups.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  upcomingTopupsData: PropTypes.objectOf(PropTypes.any),
  getUpcomingTopups: PropTypes.func,
  fetchingUpcomingTopups: PropTypes.bool,
  fetchedUpcomingTopups: PropTypes.bool,
};

UpcomingTopups.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  selectedWorkspace: {},
  upcomingTopupsData: {},
  getUpcomingTopups: _noop,
  fetchingUpcomingTopups: true,
  fetchedUpcomingTopups: true,
};

export default UpcomingTopups;
